import {
  checkoutListUrl,
  CheckoutListUrlQueryParams,
  CheckoutListUrlSortField,
  checkoutUrl,
} from "@dashboard/checkouts/urls";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import { CheckoutAuthorizeStatusEnum, usePaymentRecoveryListQuery } from "@dashboard/graphql";
import useListSettings from "@dashboard/hooks/useListSettings";
import useNavigator from "@dashboard/hooks/useNavigator";
import { usePaginationReset } from "@dashboard/hooks/usePaginationReset";
import usePaginator, {
  createPaginationState,
  PaginatorContext,
} from "@dashboard/hooks/usePaginator";
import { sectionNames } from "@dashboard/intl";
import { ListViews } from "@dashboard/types";
import createFilterHandlers from "@dashboard/utils/handlers/filterHandlers";
import createSortHandler from "@dashboard/utils/handlers/sortHandler";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { getSortParams } from "@dashboard/utils/sort";
import React from "react";
import { useIntl } from "react-intl";

import PaymentRecoveryListPage from "../../components/PaymentRecoveryListPage";
import { getSortQueryVariables } from "./sort";

export interface PaymentRecoveryListViewProps {
  params: CheckoutListUrlQueryParams;
}

/**
 * `authorizeStatus` is the whole scope of this page, not a user-adjustable
 * filter: Saleor derives it from authorized + charged, so PARTIAL/FULL is
 * exactly "money moved". A checkout that became an order no longer exists —
 * `complete_checkout` deletes it — so surviving rows are by definition
 * unconverted, and no order filter is needed.
 *
 * Filtering on the status is free; selecting it per row is not. See the note in
 * `checkouts/queries.ts`.
 */
const MONEY_MOVED = [CheckoutAuthorizeStatusEnum.PARTIAL, CheckoutAuthorizeStatusEnum.FULL];

export const PaymentRecoveryListView = ({ params }: PaymentRecoveryListViewProps) => {
  const navigate = useNavigator();
  const intl = useIntl();
  const { updateListSettings, settings } = useListSettings(ListViews.PAYMENT_RECOVERY_LIST);

  usePaginationReset(checkoutListUrl, params, settings.rowNumber);

  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: {
        authorizeStatus: MONEY_MOVED,
        search: params.query,
      },
      sort: getSortQueryVariables(params),
    }),
    [params, settings.rowNumber],
  );
  const { data, loading } = usePaymentRecoveryListQuery({
    displayLoader: true,
    variables: queryVariables,
  });
  const paginationValues = usePaginator({
    pageInfo: data?.checkouts?.pageInfo,
    paginationState,
    queryString: params,
  });
  const [, , handleSearchChange] = createFilterHandlers({
    createUrl: checkoutListUrl,
    getFilterQueryParam: async () => undefined,
    navigate,
    params,
  });
  const handleSort = createSortHandler(navigate, checkoutListUrl, params);

  return (
    <PaginatorContext.Provider value={paginationValues}>
      <WindowTitle title={intl.formatMessage(sectionNames.paymentRecovery)} />
      <PaymentRecoveryListPage
        checkouts={mapEdgesToItems(data?.checkouts) ?? []}
        disabled={loading}
        settings={settings}
        initialSearch={params.query ?? ""}
        sort={getSortParams<CheckoutListUrlQueryParams, CheckoutListUrlSortField>(params)}
        onSearchChange={handleSearchChange}
        onSort={handleSort}
        onRowClick={id => navigate(checkoutUrl(id))}
        onUpdateListSettings={updateListSettings}
      />
    </PaginatorContext.Provider>
  );
};

PaymentRecoveryListView.displayName = "PaymentRecoveryListView";
export default PaymentRecoveryListView;
